import React, { useState, useEffect, useRef } from "react";
import ReactDOM from 'react-dom';
import SharedModal from '../Widgets/SharedModal';
import { getFirestore, collection, doc, updateDoc, getDoc, setDoc, arrayUnion, onSnapshot } from "firebase/firestore";
import { app } from "../firebase";
import { useUser } from "../Widgets/UserContext";
import QRCodeShare from '../Widgets/QRCodeShare';
import { Modal, View, Platform, Pressable, Text, Dimensions } from 'react-native';
import { getMyTasselPublicUrls } from '../services/VanitySlugService';
import { SPOTLIGHT_TARGET_ATTRIBUTE, SPOTLIGHT_TARGETS } from '../Widgets/SpotlightTutorial/spotlightTargets';

const db = getFirestore(app);

// Device detection
const { width, height } = Dimensions.get('window');
const isMobile = width < 768;
const isTablet = width >= 768 && width < 1024;
const isSmallDesktop = width >= 1024 && width <= 1500;
const isLargeDesktop = width > 1500;

const Share = ({ pageOwner, pageType }) => {
	const [recipients, setRecipients] = useState("");
	const [isShared, setIsShared] = useState(false);
	const [isSharing, setIsSharing] = useState(false); // To track when sharing is in progress
	const [showShareOptions, setShowShareOptions] = useState(false);
	const [hoveredButton, setHoveredButton] = useState(null);
	const [recipientType, setRecipientType] = useState("email"); // "email" or "phone"
	const [phoneNumbers, setPhoneNumbers] = useState("");
	const [error, setError] = useState("");
	const [isError, setIsError] = useState(false);
	const [showEmailWarning, setShowEmailWarning] = useState(false);
	const { currentUser, currentUserData } = useUser();
	const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://mytassel.com';
	const publicUrls = getMyTasselPublicUrls({ pageOwner, pageOwnerId: pageOwner?.uid, baseUrl });
	var eventsPageUrl = publicUrls.events;
	var registryPageUrl = publicUrls.registry;

	let LOCAL_SERVER_URL = "http://localhost:5001/my-tassel/us-central1/api";
	let FIREBASE_SERVER_URL = "https://us-central1-my-tassel.cloudfunctions.net/api";
	// Always use production Firebase Functions (no emulators)
	const SERVER_URL = FIREBASE_SERVER_URL;

	// Debug logging to see which server is being used
	console.log('🔍 Share Debug:', {
		NODE_ENV: process.env.NODE_ENV,
		SERVER_URL,
		LOCAL_SERVER_URL,
		FIREBASE_SERVER_URL,
		USING_FIREBASE: true
	});

	const [activeTab, setActiveTab] = useState('email'); // 'email', 'phone', or 'qr'
	const [qrCodeValue, setQrCodeValue] = useState('');
	const [showQrModal, setShowQrModal] = useState(false);

	// Toast notifications (feature flagged)
	const [toastMessage, setToastMessage] = useState(null);
	const [toastType, setToastType] = useState('success');
	const [toastEnabled, setToastEnabled] = useState(false);
	const toastTimer = useRef(null);

	useEffect(() => {
		const isProduction = typeof window !== 'undefined' && window.location?.hostname === 'mytassel.com';
		const env = isProduction ? 'production' : 'staging';
		const configRef = doc(db, 'config', 'features');
		const unsubscribe = onSnapshot(configRef, (snap) => {
			if (snap.exists()) {
				const flag = snap.data().shareToastNotifications;
				const value = typeof flag === 'boolean' ? flag : flag?.[env] ?? false;
				setToastEnabled(value);
			}
		}, () => {});
		return () => unsubscribe();
	}, []);

	const showToast = (message, type = 'success') => {
		if (toastTimer.current) clearTimeout(toastTimer.current);
		setToastMessage(message);
		setToastType(type);
		toastTimer.current = setTimeout(() => setToastMessage(null), 2500);
	};

	const handleRecipientsChange = (e) => {
		setError(""); // Clear any previous errors
		setIsError(false); 
		if (recipientType === "email") {
			setRecipients(e.target.value);
		} else {
			setPhoneNumbers(e.target.value);
		}
	};

	const handleShare = async () => {
		if (recipientType === "email") {
			setShowEmailWarning(true); // Show warning first
		} else {
			// SMS is coming soon, no action needed
		}
	};

	const trackShare = async (shareData) => {
		try {
			if (!currentUser || !currentUserData) {
				// If no user is logged in, just proceed without tracking
				return;
			}

			// Use email as document ID and include more user information
			const shareAnalyticsRef = doc(db, "shareAnalytics", currentUserData.email.toLowerCase());
			const shareAnalyticsDoc = await getDoc(shareAnalyticsRef);
			
			const newShare = {
				recipient: shareData.recipients.join(", "),
				date: new Date().toISOString(),
				type: shareData.type,
				sharedPageType: pageType || 'announcement',
				pageOwnerUid: pageOwner.uid,
				senderName: `${currentUserData.firstName} ${currentUserData.lastName}`,
				senderEmail: currentUserData.email.toLowerCase()
			};

			if (shareAnalyticsDoc.exists()) {
				const currentData = shareAnalyticsDoc.data();
				await updateDoc(shareAnalyticsRef, {
					totalShares: (currentData.totalShares || 0) + 1,
					[`${shareData.type}Shares`]: arrayUnion(newShare),
					lastShareDate: new Date().toISOString(),
					userId: currentUser.uid,
					userEmail: currentUserData.email.toLowerCase(),
					firstName: currentUserData.firstName,
					lastName: currentUserData.lastName
				});
			} else {
				await setDoc(shareAnalyticsRef, {
					userId: currentUser.uid,
					userEmail: currentUserData.email.toLowerCase(),
					firstName: currentUserData.firstName,
					lastName: currentUserData.lastName,
					totalShares: 1,
					emailShares: shareData.type === 'email' ? [newShare] : [],
					smsShares: shareData.type === 'sms' ? [newShare] : [],
					lastShareDate: new Date().toISOString()
				});
			}

			
		} catch (error) {
			
		}
	};

	const handleShareViaEmail = async () => {
		setIsSharing(true);
		const recipientEmails = recipients.split(",").map((email) => email.trim());
		try {
			const response = await fetch(`${SERVER_URL}/share-user-pages`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					recipients: recipientEmails,
					senderFirstName: pageOwner.firstName,
					senderLastName: pageOwner.lastName,
					eventsPageUrl,
					registryPageUrl,
					uic: pageOwner.uic,
				}),
			});

			const data = await response.json();

				if (response.ok) {
					// Track share analytics
					await trackShare({
						type: 'email',
						recipients: recipientEmails
					});

					// Capture contacts for reminder system (all shares)
					try {
						// Create contact objects for successful emails
						const successfulEmails = data.results && data.results.length > 0 
							? data.results.map(result => result.email || result.to)
							: recipientEmails;
						
						const contactsToAdd = successfulEmails.map(email => ({
							email: email.toLowerCase(),
							firstName: '',
							lastName: '',
							phone: null,
							source: 'email_share'
						}));
						
						// Import constants to get the correct server URL
						const { SERVER_URL } = await import('../config.js');
						
						const contactResponse = await fetch(`${SERVER_URL}/add-shared-contacts`, {
							method: 'POST',
							headers: {
								'Content-Type': 'application/json'
							},
							body: JSON.stringify({
								pageOwnerId: pageOwner.uid,
								contacts: contactsToAdd
							})
						});
						
						if (!contactResponse.ok) {
							throw new Error(`HTTP ${contactResponse.status}: ${contactResponse.statusText}`);
						}
						
						const contactResult = await contactResponse.json();
						if (contactResult.success) {
							console.log('Contacts captured for reminders:', contactResult);
						} else {
							throw new Error(contactResult.error || 'Failed to add shared contacts');
						}
					} catch (contactError) {
						console.error('Contact capture failed:', contactError);
						// Don't fail the share process if contact capture fails
					}

				if (data.errors && data.errors.length > 0) {
					// Some emails failed
					if (data.results && data.results.length > 0) {
						setIsShared(true);
						setError(`${data.results.length} email(s) sent successfully, but ${data.errors.length} failed. Please check the email addresses and try again for the failed ones.`);
					} else {
						setError("Failed to send any emails. Please check the addresses and try again.");
					}
					setIsError(true);
			} else {
				// All emails sent successfully
				setIsShared(true);
				setIsError(false);
				setError("");
				setShowShareOptions(false);
				setRecipients("");
				if (toastEnabled) {
					showToast("Email invitations sent successfully!");
				} else {
					alert("Invitations sent successfully!");
				}
			}
			} else {
				setError(data.error || "Failed to send invitations. Please try again.");
				setIsError(true);
			}
		} catch (error) {
			
			setError("An error occurred while sending invitations. Please try again.");
			setIsError(true);
		} finally {
			setIsSharing(false);
		}
	};

	const handleShareViaSMS = async (phoneList) => {
		try {
			// Basic phone number validation before sending
			const validPhoneList = phoneList.filter(phone => {
				const cleaned = phone.replace(/\D/g, '');
				return cleaned.length >= 10 && cleaned.length <= 11;
			});

			if (validPhoneList.length === 0) {
				setError("Please enter valid phone numbers (e.g., 123-456-7890)");
				setIsError(true);
				return;
			}

			const response = await fetch(`${SERVER_URL}/share-via-sms`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					recipients: validPhoneList,
					senderFirstName: pageOwner.firstName,
					senderLastName: pageOwner.lastName,
					eventsPageUrl,
					registryPageUrl,
					uic: pageOwner.uic,
				}),
			});

			const data = await response.json();

			if (response.ok) {
				// Track share analytics
				await trackShare({
					type: 'sms',
					recipients: validPhoneList
				});

				// Capture phone contacts for reminder system (all shares)
				try {
					// Create contact objects for successful SMS sends
					const successfulPhones = data.results && data.results.length > 0 
						? data.results.map(result => result.phone || result.to)
						: validPhoneList;
					
					const contactsToAdd = successfulPhones.map(phone => ({
						email: '',
						firstName: '',
						lastName: '',
						phone: phone,
						source: 'sms_share'
					}));

					// Import constants to get the correct server URL
					const { SERVER_URL } = await import('../config.js');
					
					const contactResponse = await fetch(`${SERVER_URL}/add-shared-contacts`, {
						method: 'POST',
						headers: {
							'Content-Type': 'application/json'
						},
						body: JSON.stringify({
							pageOwnerId: pageOwner.uid,
							contacts: contactsToAdd
						})
					});
					
					if (!contactResponse.ok) {
						throw new Error(`HTTP ${contactResponse.status}: ${contactResponse.statusText}`);
					}
					
					const contactResult = await contactResponse.json();
					if (contactResult.success) {
						console.log('SMS contacts captured for reminders:', contactResult);
					} else {
						throw new Error(contactResult.error || 'Failed to add shared contacts');
					}
				} catch (contactError) {
					console.error('SMS contact capture failed:', contactError);
					// Don't fail the share process if contact capture fails
				}

				if (data.errors && data.errors.length > 0) {
					// Some messages failed
					if (data.results && data.results.length > 0) {
						setIsShared(true);
						setError(`${data.results.length} message(s) sent successfully, but ${data.errors.length} failed. Please check the phone numbers and try again for the failed ones.`);
					} else {
						setError("Failed to send messages. Please check the phone numbers and try again.");
					}
					setIsError(true);
			} else {
				// All messages sent successfully
				setIsShared(true);
				setIsError(false);
				setError("");
				setShowShareOptions(false);
				setPhoneNumbers("");
				if (toastEnabled) {
					showToast("SMS invitations sent successfully!");
				}
			}
			} else {
				setError(data.error || "Failed to send SMS invitations. Please try again.");
				setIsError(true);
			}
		} catch (error) {
			
			setError("An error occurred while sending invitations. Please try again.");
			setIsError(true);
		}
	};

	const handleCopyLink = () => {
		let urlToCopy = pageType === "Registry" ? registryPageUrl : eventsPageUrl;

		if (pageOwner.isFriendsListPrivate) {
			urlToCopy = urlToCopy + (pageOwner.uic ? `?uic=${pageOwner.uic}` : '');
		} 

		navigator.clipboard.writeText(urlToCopy)
			.then(() => alert("Link copied to clipboard!"))
			.catch(() => { /* Silent fail for clipboard */ });

		setShowShareOptions(false); // Hide the share options
	};

	const toggleShareOptions = () => {
		if (!isSharing) { // Prevent toggling while sharing is in progress
			setShowShareOptions(!showShareOptions);
			setIsShared(false); // Reset share status if user wants to share again
		}
	};

	const generateQrCodeUrl = () => {
		let urlToCopy = pageType === "Registry" ? registryPageUrl : eventsPageUrl;
		if (pageOwner.isFriendsListPrivate) {
			urlToCopy = urlToCopy + (pageOwner.uic ? `?uic=${pageOwner.uic}` : '');
		}
		// Add a random string to make the QR unique if needed
		const randomStr = Math.random().toString(36).substring(2, 10) + Date.now();
		return urlToCopy + (urlToCopy.includes('?') ? '&' : '?') + 'qr=' + randomStr;
	};

	return (
		<div {...{ [SPOTLIGHT_TARGET_ATTRIBUTE]: pageType === "Registry" ? SPOTLIGHT_TARGETS.REGISTRY_SHARE : undefined }} style={{ 
			marginTop: 0,
			width: isMobile || isTablet ? '100%' : 'auto',
			maxWidth: isSmallDesktop ? 400 : isLargeDesktop ? 500 : '100%',
			marginLeft: isSmallDesktop || isLargeDesktop ? 'auto' : undefined,
			marginRight: isSmallDesktop || isLargeDesktop ? 'auto' : undefined,
			overflow: 'hidden',
			display: isSmallDesktop ? 'flex' : undefined,
			justifyContent: isSmallDesktop ? 'center' : undefined,
		}}>
			{!showShareOptions && (
				<div style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					width: isMobile ? '100%' : isTablet ? '100%' : 400,
					margin: isMobile || isTablet ? undefined : '0 auto',
					padding: isMobile ? '0 10px' : '0 20px',
				}}>
					<button 
						onClick={toggleShareOptions} 
						style={{
							...styles.eventsButton,
							...(hoveredButton === 'share' && styles.eventsButtonHover)
						}}
						onMouseEnter={() => setHoveredButton('share')}
						onMouseLeave={() => setHoveredButton(null)}
					>
						<div style={styles.buttonText}>
							<p style={{ margin: 0 }}>Share {pageOwner.firstName}'s</p>
							<p style={{ margin: 0 }}>{pageType} Page</p>
						</div>
					</button>
				</div>
			)}
			
			{showShareOptions && (
				<>
					{isSharing && <p style={styles.successMessage}>Sending invitations...</p>}
					{isShared && !isError && <p style={styles.successMessage}>Shared successfully!</p>}
					{isError && <p style={styles.errorMessage}>{error}</p>}
					
					<div style={{
						...styles.recipientTypeContainer,
						padding: isMobile ? '0 10px' : '0 20px'
					}}>
						<button 
							onClick={() => {
								setRecipientType("email");
								setActiveTab('email');
								setError("");
								setIsError(false);
							}}
							style={{
								...styles.typeButton,
								...(activeTab === "email" ? styles.activeTypeButton : {})
							}}
						>
							Email
						</button>
						<button 
							onClick={() => {
								setRecipientType("phone");
								setActiveTab('phone');
								setError("");
								setIsError(false);
							}}
							style={{
								...styles.typeButton,
								...(activeTab === "phone" ? styles.activeTypeButton : {})
							}}
						>
							SMS
						</button>
						<Pressable
							onPress={() => {
								setActiveTab('qr');
								setShowQrModal(true);
								setQrCodeValue && setQrCodeValue(generateQrCodeUrl());
							}}
							style={{
								...styles.typeButton,
								...(activeTab === "qr" ? styles.activeTypeButton : {})
							}}
						>
							<Text style={activeTab === "qr" ? styles.activeTypeButtonText : styles.typeButtonText}>QR</Text>
						</Pressable>
					</div>

					{/* Main tab content area, always inside the Share box */}
					<div style={{
						width: '100%',
						flex: 1,
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'flex-start',
						padding: isMobile ? '0 10px' : '0 20px'
					}}>
						{activeTab === 'email' && (
							<>
								<input 
									type="text" 
									placeholder="Enter recipient emails, separated by commas"
									value={recipients}
									onChange={handleRecipientsChange}
									style={{
										...styles.input,
										...(isError && styles.inputError),
										fontSize: isMobile ? 14 : 16,
										padding: isMobile ? '12px' : '10px',
									}}
								/>
								<div style={{
									...styles.buttonContainer,
									flexDirection: isMobile ? 'column' : 'row',
									gap: isMobile ? '8px' : '10px',
									width: '100%',
									flexWrap: 'nowrap',
									justifyContent: 'center',
									alignItems: 'center',
								}}>
									<button 
										onClick={handleShare} 
										style={{
											...styles.button,
											fontSize: isMobile ? 14 : 16,
											padding: isMobile ? '12px 20px' : '10px 20px',
										}} 
										disabled={isSharing || !recipients.trim()}
									>
										Share via Email
									</button>
									<button 
										onClick={handleCopyLink} 
										style={{
											...styles.button,
											fontSize: isMobile ? 14 : 16,
											padding: isMobile ? '12px 20px' : '10px 20px',
										}}
									>
										Copy Page Link
									</button>
								</div>
							</>
						)}
						{activeTab === 'phone' && (
							<>
								<input 
									type="text" 
									placeholder="Enter phone numbers, separated by commas"
									value={phoneNumbers}
									onChange={handleRecipientsChange}
									style={{
										...styles.input,
										...(isError && styles.inputError),
										fontSize: isMobile ? 14 : 16,
										padding: isMobile ? '12px' : '10px',
									}}
								/>
								<div style={{
									...styles.buttonContainer,
									flexDirection: isMobile ? 'column' : 'row',
									gap: isMobile ? '8px' : '10px',
									width: '100%',
									flexWrap: 'nowrap',
									justifyContent: 'center',
									alignItems: 'center',
								}}>
									<button 
										onClick={() => {
											const phoneList = phoneNumbers.split(',').map(phone => phone.trim());
											handleShareViaSMS(phoneList);
										}} 
										style={{
											...styles.button,
											fontSize: isMobile ? 14 : 16,
											padding: isMobile ? '12px 20px' : '10px 20px',
										}} 
										disabled={isSharing || !phoneNumbers.trim()}
									>
										Share via SMS
									</button>
									<button 
										onClick={handleCopyLink} 
										style={{
											...styles.button,
											fontSize: isMobile ? 14 : 16,
											padding: isMobile ? '12px 20px' : '10px 20px',
										}}
									>
										Copy Page Link
									</button>
								</div>
							</>
						)}
					</div>
				</>
			)}

			<SharedModal 
				isVisible={showEmailWarning}
				onClose={() => setShowEmailWarning(false)}
				title="Heads Up! Check That Junk Folder!"
				message="Sometimes our emails like to play hide and seek and might end up in a spam or junk folder! Let your friends and family know to give that folder a quick peek. We promise we're not spam - just here to celebrate your big moment!"
				confirmText="Got it, Send Email"
				onConfirm={() => {
					setShowEmailWarning(false);
					handleShareViaEmail();
				}}
			/>

			<Modal
				visible={showQrModal}
				transparent={true}
				animationType="fade"
				onRequestClose={() => {
					setShowQrModal(false);
					setShowShareOptions(false);
				}}
			>
				<View style={{
					flex: 1,
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: 'rgba(0,0,0,0.5)',
					...(Platform.OS === 'web' ? { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 } : {})
				}}>
					<View style={{
						backgroundColor: '#fefde2',
						borderRadius: 20,
						padding: isMobile ? 16 : 24,
						alignItems: 'center',
						justifyContent: 'center',
						minWidth: isMobile ? 300 : 350,
						minHeight: isMobile ? 300 : 350,
						maxWidth: isMobile ? '90%' : 'auto',
						shadowColor: '#000',
						shadowOpacity: 0.2,
						shadowRadius: 10,
						elevation: 10,
					}}>
						<Text style={{
							color: '#4c956c',
							fontWeight: 'bold',
							fontSize: isMobile ? 16 : 18,
							marginBottom: 15,
							textAlign: 'center'
						}}>
							Share via QR Code
						</Text>
						<QRCodeShare url={qrCodeValue} size={isMobile ? 180 : 200} />
						<View style={{
							flexDirection: 'row',
							gap: 10,
							marginTop: 15,
							alignItems: 'center',
							justifyContent: 'center'
						}}>
							<Pressable 
								style={{
									borderColor: '#4c956c',
									borderWidth: 2,
									paddingHorizontal: isMobile ? 15 : 20,
									paddingVertical: isMobile ? 8 : 10,
									borderRadius: 5,
									minWidth: isMobile ? 60 : 80
								}}
								onPress={() => {
									setShowQrModal(false);
									setShowShareOptions(false);
								}}
							>
								<Text style={{ 
									color: '#4c956c', 
									fontWeight: 'bold', 
									fontSize: isMobile ? 14 : 16,
									textAlign: 'center'
								}}>Close</Text>
							</Pressable>
						</View>
					</View>
				</View>
			</Modal>
		{toastMessage && Platform.OS === 'web' && ReactDOM.createPortal(
			<div style={toastStyles.overlay}>
				<div style={{
					...toastStyles.box,
					backgroundColor: toastType === 'success' ? '#065f46' : '#991b1b',
				}}>
					<span style={toastStyles.icon}>{toastType === 'success' ? '✓' : '✕'}</span>
					{toastMessage}
				</div>
			</div>,
			document.body
		)}
		</div>
	);
};

const toastStyles = {
	overlay: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 999999,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
	box: {
		color: '#fff',
		borderRadius: 10,
		padding: '14px 28px',
		fontSize: 16,
		fontWeight: '500',
		boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
		pointerEvents: 'auto',
		textAlign: 'center',
		fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
		display: 'flex',
		alignItems: 'center',
		gap: '8px',
	},
	icon: {
		fontSize: 18,
		fontWeight: 'bold',
	},
};

// Styles (including your initial styles for eventsButton and eventsButtonText)
const styles = {
	eventsButton: {
		backgroundColor: "#fefde2",
		width: isMobile ? 200 : isTablet ? 250 : 284,
		height: isMobile ? 45 : isTablet ? 50 : 55,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		borderRadius: 40,
		border: "none",
		cursor: "pointer",
		transition: 'all 1s ease',
	},
	eventsButtonHover: {
		backgroundColor: "#e6e5cb",
		transform: 'scale(1.025)',
	},
	input: {
		margin: "10px 0",
		padding: isMobile ? "12px" : "10px",
		borderRadius: "5px",
		border: "1px solid #ccc",
		width: isMobile ? "90%" : "100%",
		maxWidth: isMobile ? "320px" : 500,
		fontSize: isMobile ? 16 : 18,
		marginLeft: isMobile ? 'auto' : 0,
		marginRight: isMobile ? 'auto' : 0,
		display: isMobile ? 'block' : 'inline-block',
	},
	buttonContainer: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
		gap: isMobile ? "8px" : "10px",
		width: "100%",
	},
	button: {
		margin: isMobile ? "2px" : "5px",
		padding: isMobile ? "12px 16px" : "10px 20px",
		borderRadius: "5px",
		cursor: "pointer",
		backgroundColor: "#4c956c",
		color: "white",
		border: "none",
		width: "auto",
		minWidth: "120px",
		fontSize: isMobile ? 14 : 16,
		display: 'inline-block',
		maxWidth: isMobile ? '320px' : 'none',
	},
	buttonText: {
		fontFamily: "Orpheus Pro",
		fontWeight: "500",
		fontSize: isMobile ? 14 : isTablet ? 16 : 20,
		lineHeight: "1.2",
		textAlign: "center",
		color: "#4c956c",
		textShadowOffset: { width: 3, height: 3 },
		textShadowRadius: 6,
		textShadowColor: "rgba(0, 0, 0, 0.09)",
	},
	successMessage: {
		color: "#fefde2",
		marginTop: "20px",
		textAlign: "center",
	},
	recipientTypeContainer: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
		gap: isMobile ? "6px" : "10px",
		marginBottom: isMobile ? "15px" : "10px",
		width: "100%",
		flexWrap: isMobile ? "wrap" : "nowrap",
	},
	typeButton: {
		padding: isMobile ? '8px 10px' : '8px 16px',
		border: '1px solid #4c956c',
		borderRadius: '20px',
		background: 'white',
		color: '#4c956c',
		cursor: 'pointer',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		margin: '0',
		minHeight: isMobile ? 32 : 36,
		minWidth: isMobile ? 60 : 60,
		fontSize: isMobile ? 12 : 16,
		boxSizing: 'border-box',
		width: isMobile ? 'auto' : 'auto',
	},
	activeTypeButton: {
		background: '#4c956c',
		color: 'white',
		minHeight: isMobile ? 32 : 36,
		minWidth: isMobile ? 60 : 60,
		fontSize: isMobile ? 12 : 16,
		boxSizing: 'border-box',
	},
	typeButtonText: {
		color: '#4c956c',
		fontWeight: 'normal',
		fontSize: isMobile ? 12 : 16,
	},
	activeTypeButtonText: {
		color: '#fff',
		fontWeight: 'bold',
		fontSize: isMobile ? 12 : 16,
	},
	inputError: {
		borderColor: "red",
	},
	errorMessage: {
		color: "red",
		marginTop: "10px",
		textAlign: "center",
	},
};

export default Share;
